/**
 * Personal statistics. FR-407.
 * See docs/12-derivation-and-prediction.md Section 6.
 *
 * Pure, and computed from the entry set on every read — nothing here is stored
 * (FR-001).
 *
 * ## Two naming decisions that are not cosmetic
 *
 * **"Typical", not "average".** These are medians. Labelling a median "average"
 * would be a small lie that compounds: a user comparing Thana's number against
 * one from another app, or against something a clinician said, deserves to know
 * which statistic they are looking at.
 *
 * **"Unusual", not "irregular".** Cycles outside the plausible range are
 * reported as a separate count and never characterised. FR-411 and the legal
 * map Section 4: "your cycles are irregular" is a step toward a clinical
 * conclusion, and it lands badly on someone with PCOS or endometriosis who
 * already knows. The number is shown; what it means is theirs to decide.
 */
import { validCycles } from './cycles'
import { mad, median } from './math'
import type { Cycle, DayEntry, DerivedCycles } from './types'

export interface CycleStatistics {
  /** Completed cycles, both plausible and not. */
  cyclesRecorded: number
  /** Cycles inside the plausible range — the ones the numbers below rest on. */
  cyclesUsed: number
  /**
   * Completed cycles outside the plausible range. Reported so the user can see
   * that some data was set aside, never labelled abnormal.
   */
  cyclesUnusual: number

  /**
   * The cycle-length summary, present only when there is at least one valid
   * cycle to compute it from.
   *
   * Grouped into one optional object rather than four optional numbers so the
   * invariant "these four exist together or not at all" is expressed in the
   * type. As four separate optionals, every consumer needed either four checks
   * or four non-null assertions — and an assertion is a promise the compiler
   * cannot keep if this function ever changes.
   */
  cycleSummary?: {
    /** Median cycle length in whole days. */
    typicalLength: number
    shortest: number
    longest: number
    /** Median absolute deviation of cycle length, whole days. */
    variation: number
  }

  /** Median period length in whole days. Independent of the summary above. */
  typicalPeriodLength?: number

  /** Days with an entry of any kind, including days logged with nothing. */
  daysLogged: number
  /** Days with recorded bleeding. */
  daysWithFlow: number
  /** 1-indexed day within the current cycle, when one is in progress. */
  currentCycleDay?: number
}

export function computeStatistics(
  derived: DerivedCycles,
  entries: readonly DayEntry[],
): CycleStatistics {
  const usable = validCycles(derived.cycles)

  const stats: CycleStatistics = {
    cyclesRecorded: derived.cycles.length,
    cyclesUsed: usable.length,
    cyclesUnusual: derived.cycles.length - usable.length,
    daysLogged: entries.length,
    daysWithFlow: entries.filter((e) => e.flow !== undefined && e.flow !== 'none').length,
  }

  if (derived.currentCycle) stats.currentCycleDay = derived.currentCycle.dayOfCycle

  // Every statistic below rests on valid cycles only. With none, they are
  // absent rather than zero: "0 days" would read as a measurement, and a
  // measurement is exactly what this is not.
  if (usable.length > 0) {
    const lengths = usable.map((c) => c.length)
    stats.cycleSummary = {
      typicalLength: median(lengths),
      shortest: Math.min(...lengths),
      longest: Math.max(...lengths),
      variation: mad(lengths),
    }
  }

  // Period length comes from every completed period, including those belonging
  // to an unusual cycle: a 100-day gap says nothing about how long the bleeding
  // that started it lasted.
  const periodLengths = completedPeriodLengths(derived)
  if (periodLengths.length > 0) {
    stats.typicalPeriodLength = median(periodLengths)
  }

  return stats
}

/**
 * Period lengths, excluding a period that is still in progress.
 *
 * An ongoing period looks short simply because it has not finished, and
 * including it would drag the median down every month for the few days the
 * user is bleeding.
 */
function completedPeriodLengths(derived: DerivedCycles): number[] {
  const current = derived.currentPeriod
  return derived.periods
    .filter((p) => current === undefined || p.start !== current.start)
    .map((p) => p.length)
}

/**
 * Cycles that fall outside the plausible range, for display.
 *
 * Returned so the UI can show them neutrally — as dates and a number of days,
 * with the reason they were set aside stated as a fact about the data rather
 * than about the person (FR-411).
 */
export function unusualCycles(derived: DerivedCycles): Cycle[] {
  return derived.cycles.filter((c) => !c.valid)
}
